import { Component, HostListener, Injector, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { ClearNotifications, NotificationState } from '@platform-frontend/util';
import { filter, tap } from 'rxjs/operators';
import { ActiveModal } from '../active-modal';
import { ModalCloseResult } from '../modal-close-result';
import { ModalContainerController } from '../modal-container-controller';
import { ModalData } from '../modal-data';
import { ModalHostDirective } from '../modal-host.directive';
import { ModalRegistryService } from '../modal-registry.service';
import { ModalReloadDataService } from '../modal-reload-data.service';

@UntilDestroy()
@Component({
  selector: 'yat-modal-container',
  templateUrl: './modal-container.component.html',
  styleUrls: ['./modal-container.component.scss']
})
export class ModalContainerComponent implements OnInit {
  constructor(
    private modalContainerController: ModalContainerController,
    private modalRegistryService: ModalRegistryService,
    private modalReloadDataService: ModalReloadDataService,
    private router: Router,
    private store: Store
  ) {}

  @ViewChild(ModalHostDirective, { static: true })
  yatModalHost: ModalHostDirective;

  private activeModal?: ActiveModal;

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd && (this.activeModal?.isClosable ?? true)),
        tap(() => this.activeModal?.close()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  loadComponent(modalIdentifier: symbol, data: ModalData, isClosable?: boolean): void {
    const viewContainerRef = this.yatModalHost.viewContainerRef;
    viewContainerRef.clear();

    const activeIdentifier = this.activeModal?.getIdentifier();
    const closable = isClosable ?? this.activeModal?.isClosable ?? true;

    this.activeModal = this.buildActiveModal(modalIdentifier, data, closable);
    const injector = Injector.create({
      providers: [{ provide: ActiveModal, useFactory: () => this.activeModal, deps: [] }]
    });
    viewContainerRef.createComponent(this.modalRegistryService.get(modalIdentifier), { injector });

    const openedModalWithRefreshNotification = this.store.selectSnapshot(NotificationState.hasRefreshNotifications) && !activeIdentifier;
    const clearNotificationsNotPreventedOnModalChange =
      modalIdentifier !== activeIdentifier && (typeof data !== 'object' || data?.clearNotificationsIfModalOpen !== false);
    const clearNotificationsRequested = typeof data === 'object' && data?.clearNotificationsIfModalOpen === true;

    if (!openedModalWithRefreshNotification && (clearNotificationsNotPreventedOnModalChange || clearNotificationsRequested)) {
      this.store.dispatch(new ClearNotifications());
    }

    this.modalReloadDataService.setReloadData(modalIdentifier, data, closable);
  }

  private buildActiveModal(identifier: symbol, data: ModalData, isClosable: boolean): ActiveModal {
    return new ActiveModal(
      identifier,
      data,
      (result?: ModalCloseResult) => this.modalContainerController.close(result),
      (result?: ModalCloseResult) => this.modalContainerController.dismiss(result),
      (modalIdentifier: symbol, newData: ModalData) => this.loadComponent(modalIdentifier, newData),
      isClosable
    );
  }

  @HostListener('window:keydown', ['$event'])
  keypress($event: KeyboardEvent): void {
    if ($event.key === 'Escape' && this.activeModal?.isClosable) {
      this.modalContainerController.dismiss({ reason: 'ESC' });
    }
  }
}
